import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { toJS } from 'mobx'
import { FormControlLabel, Grid, Switch, Typography, withStyles } from '@material-ui/core';
import Badge from '@material-ui/core/Badge';
import Modal from '@material-ui/core/Modal';

import Tooltip from '@material-ui/core/Tooltip';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Button from '@material-ui/core/Button';
import TextField from "@material-ui/core/TextField";
import SearchIcon from '@material-ui/icons/Search';
import Autocomplete from "@material-ui/lab/Autocomplete";
import DataFrameDownload from './utils/DataFrameDownload';
import MultiSelectList from '../store/MultiSelectList';
import { listToString } from '../utils';

const styles = (theme) => ({
  primaryButton:{
    border: '1px solid lightgrey',
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    display: 'inline',
    textAlign: 'center',
    width: theme.spacing(81),
    height: theme.spacing(81),
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2, 4, 3),
  },
  root: {
    display :'inline-grid',
    height: '100%',
  },
  container: {
    flexFlow: 'row',
    display: 'flex',
    height: theme.spacing(63),
    "& .column-container": {
      height: 'inherit',
      textAlign: 'left',
      display: 'inline',
      marginLeft: 10,
      marginRight: 10,
      "& .list-label": {
        font: 'inherit',
        width: 'fit-content',
        textAlign: 'left',
        position: "relative",
        top: 10,
        left: 15,
        backgroundColor: "#fff",
        padding: "0 8px"
      },
    },
    "& .paste-mode-spacing": {
      "&::before": {
        content: `" "`,
        backgroundColor: 'transparent',
        textAlign: 'left',
        position: "relative",
        top: 10,
        left: 15,
        padding: "0 8px",
      },
    },
  },
  listBox: {
    border: '1px solid grey',
    borderRadius: '5px',
    height: theme.spacing(59),
    maxHeight: theme.spacing(59),
    padding: 0,
    display: 'table',
    margin: 0,
    width: '100%',
    "& .list-button": {display: 'table-footer-group'},
  },
  shortList: {
    maxHeight: theme.spacing(44),
  },
  longList: {
    maxHeight: theme.spacing(50),
  },


  actions: {
    height: theme.spacing(5),
    flexWrap: 'nowrap',
    "& .actions-paste": {
      display: 'flex',
      width: '50%',
      float: 'left',
      justifyContent: 'center',
      "& .paste-mode-switch": {
        margin: 0,
        width: 'inherit',
      }
    },
    "& .actions-download": {
      display: 'flex',
      width: '50%',
      float: 'right',
      justifyContent: 'center',
    }
  },
  downloadButton: {
    padding: 0,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    // height: 'auto',
    width: 'inherit',
    border: '1px solid #bdbdbd47',
    boxShadow: '0 2px 4px 0 #bdbdbd47',
  }
});


@observer
class ListInput extends React.Component {
  static propTypes = {
    multiSelectList: PropTypes.instanceOf(MultiSelectList).isRequired,
    InputProps: PropTypes.shape(),
  };

  static defaultProps = {
    InputProps: {},
  };

  render() {
    const { InputProps, multiSelectList, placeholder, classes, ...props } = this.props;
    InputProps.startAdornment = undefined
    return (
      <ListItem>
        <TextField
          {...props}
          placeholder={'Search'}
          fullWidth
          InputProps={{...InputProps}}
        />
      </ListItem>
    );
  }
}

@withStyles(styles)
@observer
class MultiSelectListView extends React.Component {
  static propTypes = {
    selectedValues: PropTypes.array.isRequired,
    multiSelectList: PropTypes.instanceOf(MultiSelectList),
    enableSelectAll: PropTypes.bool,
    downloadConfig: PropTypes.object,
  };


  /** 
   * downloadConfig is an optional object used to configure download funcitonality on selection
   * @param href api URL
   * @param permission: Permissions for eg, "athena.download_raw_packs_bom",
   * @param param: payload parameter
   */
  static defaultProps = {
    enableSelectAll: false,
    downloadConfig: null,
  };
  
  renderListContainer() {
    const { multiSelectList, classes, enableSelectAll } = this.props;
    const availableOptions = toJS(multiSelectList.getAvailableOptions())
    const checkedOptions = toJS(multiSelectList.getCheckedOptions())
    return (
      <Grid item container spacing={2} className={classes.container}>
        {multiSelectList.isPasteMode? <>
          <Grid item container spacing={2} className="column-container paste-mode-spacing">
            <Grid container item className={classes.listBox}>
              <Grid item>
                <TextField
                  multiline
                  fullWidth
                  rows={18}
                  placeholder="Paste list here.."
                  value={listToString(multiSelectList.getTransferList())}
                  InputProps={{ disableUnderline: true}}
                  onChange={(event) => {
                    multiSelectList.updateTransferList(event)
                  }}
                />
              </Grid>
              <Grid item className="list-button">
                <Button
                  size="large"
                  fullWidth
                  disabled={multiSelectList.getTransferList().length === 0}
                  onClick={()=> {
                    multiSelectList.enableSearch()
                    multiSelectList.handleTransfer()
                  }}
                >
                    Transfer
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </> : <>
          <Grid container item spacing={2} className="column-container">
            <Typography className="list-label">Available ({multiSelectList.getAvailableOptions().length})</Typography>
            <Grid container item className={classes.listBox}>
              <Grid item>
                <List disablePadding>
                  <Autocomplete
                    freeSolo
                    multiple
                    fullWidth
                    name='Available Options'
                    options={availableOptions}
                    onChange={(event, values) => multiSelectList.addCheckedOption(values)}
                    disableClearable
                    disableCloseOnSelect
                    open
                    renderInput={props => 
                      <ListInput 
                        {...props} 
                        multiSelectList={multiSelectList} 
                      />
                    }
                    size="small"
                    classes={{
                      listbox: enableSelectAll? classes.shortList : classes.longList,
                    }}
                  />
                </List>
              </Grid>
              {enableSelectAll && 
                (<Grid item className="list-button">
                  <Button
                    size="large"
                    fullWidth
                    onClick={()=> multiSelectList.selectAllOptions()}
                  >
                    Select All
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </>}
        <Grid container item spacing={2} className="column-container">
          <Typography className="list-label">Selected ({multiSelectList.getCheckedOptions().length})</Typography>
          <Grid container item className={classes.listBox}>
            <Grid item>
              <List disablePadding>
                <Autocomplete
                  freeSolo
                  multiple
                  name='Selected Options'
                  options={checkedOptions}
                  onChange={(event, values)=> multiSelectList.removeCheckedOption(values)}
                  disableClearable
                  disableCloseOnSelect
                  open
                  renderInput={props => 
                    <ListInput 
                      {...props} 
                      multiSelectList={multiSelectList} 
                    />
                  }                    
                  size="small"
                  classes={{
                    listbox: classes.shortList,
                  }}
                />
              </List>
            </Grid>
            <Grid item className="list-button">
              <Button
                size="large"
                fullWidth
                onClick={()=>{
                  multiSelectList.clear()
                  multiSelectList.enableSearch()
                }}
                disabled={multiSelectList.getCheckedOptions().length === 0}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }


  renderActions() {
    const { classes, multiSelectList, downloadConfig } = this.props;
    return (
      <>
      <Grid container item className={classes.actions}>
        <Grid item className="actions-paste">
          <FormControlLabel
            className='paste-mode-switch'
            control={
              <Switch 
                checked={multiSelectList.isPasteMode} 
                onChange={() => multiSelectList.switchPasteMode()}
                color="primary"
              />
            }
            label={'Paste Mode'}
          />
        </Grid>
        <Grid item className="actions-download">
          {downloadConfig !== null &&
            <DataFrameDownload
              tooltipText={multiSelectList.getDownloadTooltipTitle()}
              href={downloadConfig.href}
              permission={downloadConfig.permission}
              disabled={multiSelectList.isDownloadDisabled}
              buttonConfig= {{
                type: "text",
                class: classes.downloadButton,
              }}
              urlConfig={
                multiSelectList.isSelectAll? 
                  `&${downloadConfig.param}=all` : multiSelectList.getCheckedOptions().map(val=>`&${downloadConfig.param}=${val}`).join("")
              }
            />
          }
        </Grid>
      </Grid> 
      <Grid item>
        <Tooltip 
          title={multiSelectList.getSearchTooltipTitle()} 
          placement="top" 
          arrow
        >
          <span>
            <Button
              size="large"
              fullWidth
              onClick={(event)=> {
                multiSelectList.close(event)
                const values = multiSelectList.checkedOptions;
                this.props.onValueChange(event, values)
              }}
              disabled={multiSelectList.isSearchDisabled}
            >
                Search 
            </Button>
          </span>
        </Tooltip>
      </Grid>
    </>
    )
  }

  render() {
    const { multiSelectList, selectedValues, classes } = this.props;
    console.log(toJS(multiSelectList))
    return (
      <>
        <Badge badgeContent={multiSelectList.isValidArray(selectedValues)? selectedValues.length : 0} color="primary">
          <Button
            className={classes.primaryButton}
            variant="contained"
            onClick={event => multiSelectList.open(event)}
            startIcon={<SearchIcon />}
          >   
                {multiSelectList.name}
          </Button>
        </Badge>
        <Modal
          open={multiSelectList.isOpen}
          onClose={() => {
            multiSelectList.revertChanges(selectedValues)
            multiSelectList.close()
          }}
          className={classes.modal}
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
        >
          <div className={classes.paper}>
            <Grid container className={classes.root}>
              <Grid item>
                <Typography variant='h6'>
                  Select {multiSelectList.name}
                </Typography>
              </Grid>
              {this.renderListContainer()}
              {this.renderActions()}
            </Grid>
          </div>
        </Modal>
      </>
    )
  }

}


export default MultiSelectListView;